import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { and, eq } from 'ember-truth-helpers';

interface PrismDrawerSignature {
  Args: {
    align?: 'left' | 'right';
    contentPadding?: 'default' | 'none';
    hasOverlay?: boolean;
    isOpen: boolean;
    onToggle?: () => void;
    positionTop?: number;
  };
  Blocks: {
    content: [];
    trigger: [];
  };
  Element: HTMLElement;
}

export default class PrismDrawer extends Component<PrismDrawerSignature> {
  @tracked hasOpeningAnimationFinished = this.args.isOpen ?? false;

  handleOpeningClosingAnimation = modifier(
    (_: unknown, [isOpen]: [boolean]) => {
      let timeout: number;

      if (isOpen) {
        // The opening animation takes 500ms, wait for it to be finished
        // However, if we wait the same 500ms, it feels like there is a delay in displaying of contents, therefore, we reduce this waiting time by 100ms
        timeout = setTimeout(() => {
          this.hasOpeningAnimationFinished = true;
        }, 400);
      } else if (!isOpen) {
        this.hasOpeningAnimationFinished = false;
      }

      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      return () => {
        clearTimeout(timeout);
      };
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get align() {
    return this.args.align ?? 'right';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get contentPadding() {
    return this.args.contentPadding ?? 'default';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get destinationElement() {
    const wormhole = document.getElementById('prism-wormhole');

    if (!wormhole) {
      throw new Error(
        'Prism::Drawer component requires a Prism::Wormhole component to be present in the DOM',
      );
    }

    return wormhole;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get drawerClass() {
    let drawerClass = 'drawer';

    if (this.args.isOpen) {
      drawerClass += ' drawer--expanded';

      if (this.contentPadding === 'default') {
        drawerClass += ' drawer--with-default-padding';
      }
    }

    return drawerClass;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get drawerContainerStyle() {
    return htmlSafe(`top: ${this.positionTop}px;`);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasOverlay() {
    return this.args.hasOverlay ?? false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get positionTop() {
    return this.args.positionTop ?? 0;
  }

  @action
  handleToggle(): void {
    this.args.onToggle?.();
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    {{#in-element this.destinationElement insertBefore=null}}
      <aside
        class="drawer-container
          {{if
            (eq this.align 'left')
            'drawer-container--left-aligned'
            'drawer-container--right-aligned'
          }}"
        style={{this.drawerContainerStyle}}
        {{this.handleOpeningClosingAnimation @isOpen}}
        ...attributes
      >
        <div class={{this.drawerClass}}>
          {{#if (and @isOpen this.hasOpeningAnimationFinished)}}
            {{yield to="content"}}
          {{/if}}
        </div>

        {{#if (has-block "trigger")}}
          <button
            class="prism-button prism-button--brand trigger
              {{if
                (eq this.align 'left')
                'trigger--left-aligned'
                'trigger--right-aligned'
              }}"
            type="button"
            {{on "click" this.handleToggle}}
          >
            {{yield to="trigger"}}
          </button>
        {{/if}}
      </aside>
      {{#if (and @isOpen this.hasOverlay)}}
        <div class="overlay" {{on "click" this.handleToggle}} />
      {{/if}}
    {{/in-element}}
  </template>
}
